import React, { memo, useEffect, useState } from 'react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';
import { LIMITES_PRESTAMO } from './constants';

const LoanDetails = ({ formData, handleInputChange, errors, limitesExtendidos = false, apiBasePath = '/api/admin' }) => {
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [errorProductos, setErrorProductos] = useState('');

  const limites = limitesExtendidos 
    ? LIMITES_PRESTAMO.EXTENDIDO 
    : LIMITES_PRESTAMO.ESTANDAR;

  const handleWheel = (e) => {
    e.target.blur();
  };

  const emitFieldChange = (name, value) => {
    handleInputChange({
      target: {
        name,
        value,
      },
    });
  };

  const productoPermiteDecenal = (producto) => {
    if (!producto) return false;
    if (Array.isArray(producto.frecuencias_permitidas)) {
      return producto.frecuencias_permitidas.includes('decenal');
    }

    return producto.tipo_producto === 'CAMPANA' && producto.activo !== false;
  };

  const handleProductoChange = (e) => {
    const selectedId = e.target.value;
    const selectedProducto = productos.find(
      producto => String(producto.idProducto) === String(selectedId)
    ) || null;

    handleInputChange(e);
    emitFieldChange('producto', selectedProducto);

    if (productoPermiteDecenal(selectedProducto)) {
      emitFieldChange('frecuencia', 'decenal');
      return;
    }

    if (formData?.frecuencia === 'decenal') {
      emitFieldChange('frecuencia', '');
    }
  };

  // Fetch products
  useEffect(() => {
    const fetchProductos = async () => {
      setLoadingProductos(true);
      setErrorProductos('');
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}${apiBasePath}/productos?activos=1&all=1`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Error al cargar productos');
        const data = await response.json();
        setProductos(data.productos || []);
      } catch (err) {
        setErrorProductos(err.message || 'Error al cargar productos');
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [apiBasePath]);

  const handleCheckboxChange = (e) => {
    const isExtended = e.target.checked;
    const event = {
      target: {
        name: 'limitesExtendidos',
        value: isExtended
      }
    };
    handleInputChange(event);
  };

  return (
    <div className="mb-3 rounded-xl border border-red-100 bg-red-50/70 p-3 md:mb-6 md:p-5">
      <h3 className="mb-2 text-base font-semibold text-red-700 sm:text-lg md:mb-4 md:text-xl">DATOS DEL CRÉDITO</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        {/* Producto */}
        <div className="flex flex-col sticky-inputs">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">PRODUCTO</label>
          <select
            name="idProducto"
            value={formData?.idProducto || ''}
            onChange={handleProductoChange}
            className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.idProducto ? 'border-red-500' : 'border-slate-300'}`}
            disabled={loadingProductos}
          >
            <option value="">Seleccione un producto</option>
            {productos.length > 0 ? (
              productos.map(producto => (
                <option key={producto.idProducto} value={producto.idProducto}>
                  {producto.nombre} ({producto.rango_tasa})
                </option>
              ))
            ) : (
              <option disabled>No hay productos disponibles</option>
            )}
          </select>
          {loadingProductos && <p className="text-gray-500 text-sm mt-1">Cargando productos...</p>}
          {errorProductos && <p className="text-red-500 text-sm mt-1">{errorProductos}</p>}
          {errors.idProducto && <p className="text-red-500 text-sm mt-1" role="alert">{errors.idProducto}</p>}
        </div>

        {/* Abonado Por */}
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">ABONADO POR</label>
          <select
            name="abonado_por"
            value={formData?.abonado_por || 'CUENTA CORRIENTE'}
            onChange={handleInputChange}
            className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.abonado_por ? 'border-red-500' : 'border-slate-300'}`}
          >
            <option value="CUENTA CORRIENTE">CUENTA CORRIENTE</option>
            <option value="CAJA CHICA">CAJA CHICA</option>
          </select>
          {errors.abonado_por && <p className="text-red-500 text-sm mt-1" role="alert">{errors.abonado_por}</p>}
        </div>

        {/* Hora de cobranza */}
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">HORA DE COBRANZA</label>
          <input
            name="hora_cobranza"
            type="time"
            value={formData?.hora_cobranza || ''}
            onChange={handleInputChange}
            className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.hora_cobranza ? 'border-red-500' : 'border-slate-300'}`}
            aria-invalid={!!errors.hora_cobranza}
          />
          {errors.hora_cobranza && <p className="text-red-500 text-sm mt-1" role="alert">{errors.hora_cobranza}</p>}
        </div>

        {/* Importe de Crédito */}
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">IMPORTE DE CRÉDITO</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">S/</span>
            <input
              name="credito"
              type="number"
              value={formData?.credito || ''}
              onChange={handleInputChange}
              onWheel={handleWheel}
              placeholder="0.00"
              min="0"
              step="any"
              className={`w-full rounded-xl border bg-white pl-8 pr-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.credito ? 'border-red-500' : 'border-slate-300'}`}
              inputMode="decimal"
            />
          </div>
          {errors.credito && <p className="text-red-500 text-sm mt-1" role="alert">{errors.credito}</p>}
        </div>

        {/* Tasa de Interés */}
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">
            TASA DE INTERÉS (%)
          </label>
          <input
            name="interes"
            type="number"
            value={formData?.interes || ''}
            onChange={handleInputChange}
            onWheel={handleWheel}
            placeholder="0"
            min={limites.tasaInteres.min}
            max={limites.tasaInteres.max}
            step="0.01"
            className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.interes ? 'border-red-500' : 'border-slate-300'}`}
            inputMode="decimal"
            aria-invalid={!!errors.interes}
            aria-describedby={errors.interes ? "interes-error" : "interes-help"}
          />
          {errors.interes && (
            <p id="interes-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.interes}
            </p>
          )}
          <p id="interes-help" className="mt-1 text-xs text-slate-500">
            Rango: {limites.tasaInteres.min}% - {limites.tasaInteres.max}%
          </p>
        </div>

        {/* Número de Cuotas */}
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-slate-700 sm:text-sm md:mb-2">
            N° CUOTAS
          </label>
          <input
            name="cuotas"
            type="number"
            value={formData?.cuotas || ''}
            onChange={handleInputChange}
            onWheel={handleWheel}
            placeholder="0"
            min={limites.numeroCuotas.min}
            max={limites.numeroCuotas.max}
            className={`w-full rounded-xl border bg-white px-3 py-2 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${errors.cuotas ? 'border-red-500' : 'border-slate-300'}`}
            inputMode="numeric"
            aria-invalid={!!errors.cuotas}
            aria-describedby={errors.cuotas ? "cuotas-error" : "cuotas-help"}
          />
          {errors.cuotas && (
            <p id="cuotas-error" className="text-red-500 text-sm mt-1" role="alert">
              {errors.cuotas}
            </p>
          )}
          <p id="cuotas-help" className="mt-1 text-xs text-slate-500">
            Rango: {limites.numeroCuotas.min} - {limites.numeroCuotas.max} cuotas
          </p>
        </div>

        {/* Checkbox para límites extendidos */}
        <div className="flex flex-col justify-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={limitesExtendidos}
              onChange={handleCheckboxChange}
              className="mr-2 w-4 h-4 text-red-600 focus:ring-red-500 focus:ring-2 rounded transition-colors"
            />
            <span className="text-xs font-medium text-slate-700 sm:text-sm">
              Usar límites extendidos ({LIMITES_PRESTAMO.EXTENDIDO.tasaInteres.max}% tasa / {LIMITES_PRESTAMO.EXTENDIDO.numeroCuotas.max} cuotas)
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default memo(LoanDetails);